import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Plus, Edit, Trash2, X, Save, Briefcase } from 'lucide-react'
import { apiFetch } from '../../config/api'

interface JobPosting {
  id: number
  title: string
  description: string
  requirements: string | null
  location: string | null
  employment_type: string | null
  department: string | null
  status: 'Aktiv' | 'Inaktiv' | 'Geschlossen'
  created_at: string
  updated_at: string
}

export default function BewerbungManagement() {
  const [jobs, setJobs] = useState<JobPosting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    location: '',
    employment_type: '',
    department: '',
    status: 'Aktiv' as 'Aktiv' | 'Inaktiv' | 'Geschlossen',
  })
  const formRef = useRef<HTMLDivElement>(null)

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchJobs = async () => {
    try {
      setLoading(true)
      const response = await apiFetch('/jobs', {
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setJobs(data.data || [])
        setError('')
      } else {
        setError(data.error || 'Fehler beim Laden der Stellenanzeigen')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Fetch jobs error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [])

  const handleAddJob = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.description) {
      setError('Titel und Beschreibung sind erforderlich.')
      return
    }

    try {
      const response = await apiFetch('/jobs', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          requirements: formData.requirements || null,
          location: formData.location || null,
          employment_type: formData.employment_type || null,
          department: formData.department || null,
          status: formData.status,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setShowAddForm(false)
        resetForm()
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Erstellen der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add job error:', err)
    }
  }

  const handleUpdateJob = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.description) {
      setError('Titel und Beschreibung sind erforderlich.')
      return
    }

    if (!editingJob) return

    try {
      const response = await apiFetch(`/jobs/${editingJob.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          requirements: formData.requirements || null,
          location: formData.location || null,
          employment_type: formData.employment_type || null,
          department: formData.department || null,
          status: formData.status,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEditingJob(null)
        resetForm()
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Aktualisieren der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update job error:', err)
    }
  }

  const handleDeleteJob = async (jobId: number) => {
    if (!confirm('Möchten Sie diese Stellenanzeige wirklich löschen?')) {
      return
    }

    try {
      const response = await apiFetch(`/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        fetchJobs()
      } else {
        setError(data.error || 'Fehler beim Löschen der Stellenanzeige')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Delete job error:', err)
    }
  }

  const startEdit = (job: JobPosting) => {
    setEditingJob(job)
    setFormData({
      title: job.title,
      description: job.description,
      requirements: job.requirements || '',
      location: job.location || '',
      employment_type: job.employment_type || '',
      department: job.department || '',
      status: job.status,
    })
    setShowAddForm(false)
    
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const cancelEdit = () => {
    setEditingJob(null)
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      location: '',
      employment_type: '',
      department: '',
      status: 'Aktiv',
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      {/* Header mit Welcome Badge */}
      <motion.div 
        className="mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
              Stellenanzeigen verwalten
            </h1>
            <p className="mt-1 text-sm text-zinc-500">Verwalte alle Stellenanzeigen und Bewerbungen.</p>
          </div>
        {!showAddForm && !editingJob && (
          <motion.button
            onClick={() => {
              setShowAddForm(true)
              setEditingJob(null)
              resetForm()
              setTimeout(() => {
                if (formRef.current) {
                  formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }, 100)
            }}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-900 px-3 text-sm font-medium text-white shadow-sm hover:bg-zinc-800"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="h-5 w-5" />
            Neue Stellenanzeige
          </motion.button>
        )}
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingJob) && (
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 relative bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden"
        >
          
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-zinc-950">
                {editingJob ? 'Stellenanzeige bearbeiten' : 'Neue Stellenanzeige erstellen'}
              </h2>
              <motion.button
                onClick={() => {
                  setShowAddForm(false)
                  cancelEdit()
                }}
                className="text-zinc-500 hover:text-zinc-900"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>

            <form onSubmit={editingJob ? handleUpdateJob : handleAddJob} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-zinc-600 mb-2">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input-dashboard"
                    required
                    placeholder="z.B. Servicekraft (m/w/d)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 mb-2">
                    Abteilung
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="input-dashboard"
                    placeholder="z.B. Service, Küche"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 mb-2">
                    Arbeitsort
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input-dashboard"
                    placeholder="z.B. Krefeld"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 mb-2">
                    Beschäftigungsart
                  </label>
                  <input
                    type="text"
                    value={formData.employment_type}
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                    className="input-dashboard"
                    placeholder="z.B. Vollzeit, Teilzeit, Minijob"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="input-dashboard"
                  >
                    <option value="Aktiv">Aktiv</option>
                    <option value="Inaktiv">Inaktiv</option>
                    <option value="Geschlossen">Geschlossen</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-600 mb-2">
                  Beschreibung *
                </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    className="input-dashboard"
                    placeholder="Beschreibung der Stelle..."
                    required
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-600 mb-2">
                  Anforderungen
                </label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={4}
                  className="input-dashboard"
                  placeholder="Anforderungen und Qualifikationen..."
                />
              </div>

              <div className="flex items-center gap-4 pt-4">
                <motion.button
                  type="submit"
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-900 px-3 text-sm font-medium text-white shadow-sm hover:bg-zinc-800"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Save className="h-5 w-5" />
                  {editingJob ? 'Aktualisieren' : 'Erstellen'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    cancelEdit()
                  }}
                  className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Abbrechen
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Jobs Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden"
      >
        
        <div className="relative z-10">
          {loading ? (
            <div className="p-12 text-center text-zinc-600">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Briefcase className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Stellenanzeigen...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-12 text-center">
              <Briefcase className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-600">Keine Stellenanzeigen gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm"
                >
                  {/* Background Gradient */}
                  
                  {/* Job Info */}
                  <div className="relative z-10 p-5">
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-zinc-950 mb-3 line-clamp-2 group-hover:text-zinc-700 transition-colors">
                      {job.title}
                    </h3>
                    
                    {/* Meta Info */}
                    <div className="space-y-2 mb-4">
                      {job.department && (
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <span className="text-zinc-700">Abteilung:</span>
                          <span>{job.department}</span>
                        </div>
                      )}
                      {job.location && (
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <span className="text-zinc-700">Ort:</span>
                          <span>{job.location}</span>
                        </div>
                      )}
                      {job.employment_type && (
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <span className="text-zinc-700">Art:</span>
                          <span>{job.employment_type}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Description */}
                    {job.description && (
                      <p className="text-sm text-zinc-600 line-clamp-3 mb-4">
                        {job.description}
                      </p>
                    )}
                    
                    {/* Status Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
 job.status === 'Aktiv'
 ? 'bg-emerald-500/90 text-white'
 : job.status === 'Geschlossen'
 ? 'bg-gray-500/90 text-white'
 : 'bg-yellow-500/90 text-black'
 }`}>
                        {job.status}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {formatDate(job.created_at)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-200">
                      <motion.button
                        onClick={() => startEdit(job)}
                        className="inline-flex h-8 flex-1 items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-800 hover:bg-zinc-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Edit className="h-4 w-4" />
                        Bearbeiten
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteJob(job.id)}
                        className="inline-flex size-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}







